/**
 * miniAppDesign — the look of a Mini-App, chosen by the user in the Sales
 * Studio, never by the model.
 *
 * Mirror of functions/helpers/miniAppDesign.js; functions/helpers/miniAppDesign.test.js
 * fails when the two drift. Edit both together.
 */

const DESIGN_OPTIONS = {
  theme: ['light', 'dark', 'warm'],
  font: ['modern', 'editorial'],
  corners: ['soft', 'sharp', 'round'],
  layout: ['split', 'stacked'],
  brand: ['off', 'colors', 'full'],
};

const HIDEABLE = ['insight', 'scenarios', 'ticker', 'projection', 'chart', 'comparison', 'checklist', 'footnote', 'sender'];

const DEFAULT_DESIGN = {
  theme: 'light',
  font: 'modern',
  corners: 'soft',
  layout: 'split',
  brand: 'off',
  accent: '',
  hide: [],
};

const DEFAULT_ACCENT = '#2a6776';
const LOGO_URL_RE = /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/[\w.-]+\/o\/[\w%.-]+\?alt=media&token=[\w-]+$/;

const luminanceOf = (n) => {
  const lin = [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * lin[0] + 0.7152 * lin[1] + 0.0722 * lin[2];
};

/** White text sits on the accent (buttons), so light colors are rejected. */
function readableAccent(value, fallback = DEFAULT_ACCENT) {
  const s = String(value || '');
  if (!/^#[0-9a-fA-F]{6}$/.test(s)) return fallback;
  return luminanceOf(parseInt(s.slice(1), 16)) > 0.3 ? fallback : s.toLowerCase();
}

/**
 * A brand color made usable as accent: too light colors are darkened step by
 * step (keeping their hue) instead of being thrown away. '' when invalid.
 */
function brandAccent(value) {
  const s = String(value || '');
  if (!/^#[0-9a-fA-F]{6}$/.test(s)) return '';
  let n = parseInt(s.slice(1), 16);
  for (let step = 0; step < 12 && luminanceOf(n) > 0.3; step += 1) {
    n = (Math.round(((n >> 16) & 255) * 0.88) << 16) | (Math.round(((n >> 8) & 255) * 0.88) << 8) | Math.round((n & 255) * 0.88);
  }
  return `#${n.toString(16).padStart(6, '0')}`;
}

function sanitizeDesign(raw) {
  const src = raw && typeof raw === 'object' ? raw : {};
  const pick = (key) => (DESIGN_OPTIONS[key].includes(src[key]) ? src[key] : DEFAULT_DESIGN[key]);
  const hideSrc = Array.isArray(src.hide) ? src.hide : src.hide && typeof src.hide === 'object' ? Object.values(src.hide) : [];
  return {
    theme: pick('theme'),
    font: pick('font'),
    corners: pick('corners'),
    layout: pick('layout'),
    brand: pick('brand'),
    accent: src.accent ? readableAccent(src.accent, '') : '',
    hide: HIDEABLE.filter((h) => hideSrc.includes(h)),
  };
}

/**
 * The look one contact's own brand asks for, found on their website. Only
 * enums, a checked color and a logo stored in our own bucket survive.
 */
function sanitizeBrand(raw) {
  if (!raw || typeof raw !== 'object') return null;
  const pick = (key) => (DESIGN_OPTIONS[key].includes(raw[key]) ? raw[key] : '');
  const brand = {
    accent: brandAccent(raw.accent),
    theme: pick('theme'),
    font: pick('font'),
    corners: pick('corners'),
    logoUrl: LOGO_URL_RE.test(String(raw.logoUrl || '')) ? raw.logoUrl : '',
    logoOnDark: !!raw.logoOnDark,
  };
  return brand.accent || brand.theme || brand.logoUrl ? brand : null;
}

/** The user's design with the contact's brand laid over it, as far as the brand mode allows. */
function applyBrand(design, brand) {
  const base = sanitizeDesign(design);
  const b = base.brand === 'off' ? null : sanitizeBrand(brand);
  if (!b) return base;
  const out = { ...base, accent: b.accent || base.accent };
  if (base.brand === 'full') {
    if (b.theme) out.theme = b.theme;
    if (b.font) out.font = b.font;
    if (b.corners) out.corners = b.corners;
  }
  return out;
}

/** Colors the email preview card uses per theme. */
const THEME_COLORS = {
  light: { bg: '#ffffff', ink: '#0f1720', muted: '#66717c', faint: '#8b959e', panel: '#f6f7f8', line: '#e6e9ec' },
  dark: { bg: '#0d1114', ink: '#f2f5f7', muted: '#a3adb5', faint: '#7d8891', panel: '#161c20', line: '#252d33' },
  warm: { bg: '#fbf8f3', ink: '#1f1a14', muted: '#75695b', faint: '#998d7e', panel: '#f3ede3', line: '#e7dfd2' },
};

export { DESIGN_OPTIONS, HIDEABLE, DEFAULT_DESIGN, DEFAULT_ACCENT, THEME_COLORS, readableAccent, brandAccent, sanitizeDesign, sanitizeBrand, applyBrand };
